import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { findFarmsByProximity } from "../../api/search-service/searchService"
import useUserLocation from "../../hooks/useUserLocation"
import FarmCardStyle from "../../assets/stylesheets/FarmCard.module.css";
import SkeletonCard  from "../common/SkeletonCard"
import SwiperFarmCardList from "./SwiperFarmCardList";
import CarouselNavigation from "../common/CarouselNavigation";
import Logger from "../../utils/logger";
import slugify from 'slugify';

const log = Logger.create("WineryCarousel");

const wineryCarouselContainerTitle = "Winery experiences near you";
const wineryCarouselContainerSubtitle = "Wine tastings, vineyard strolls, and countryside views";

export default function WineryCarousel() {
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const prevRef   = useRef(null);
  const nextRef   = useRef(null);
  const { location, error } = useUserLocation()

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd]             = useState(false);
  const [farms, setFarms]             = useState([]);
 const [loading, setLoading] = useState(true); 

  const updateNavState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const DEFAULT_VERBOSE = false;
  const DEFAULT_PROXIMITY_DISTANCE = 10000; // 10,000 km radius
  const WINERY_CATEGORY_ID =  7
  const DEFAULT_FARM_ARRAY_LENGTH = 6;
  const DEFAULT_SORT = 'distance';
  const DEFAULT_SORT_ORDER = 'ASC';


  // DEBUG: useUserLocation (IP/GPS) 
  useEffect(() => {
    if (!location) {
      log.log('[AnimalsCarousel][Location] null', { error });
      return;
    }
    const source = location?.source ?? (location?.ip ? 'IP' : 'GPS');
    log.log(`[AnimalsCarousel][Location] from ${source}:`, location);
  }, [location, error]); 
  
  

  // when we get the coords, call the proximity endpoint
  useEffect(() => {
      if (!location) return

      setLoading(true);     
      findFarmsByProximity({
        verbose: DEFAULT_VERBOSE,
        latitude:  location.lat, 
        longitude: location.lng,
        distance:  DEFAULT_PROXIMITY_DISTANCE,
        categoryId: WINERY_CATEGORY_ID,
        limit: DEFAULT_FARM_ARRAY_LENGTH,
        sort: DEFAULT_SORT,
        order: DEFAULT_SORT_ORDER
      })
        .then(response => {
          const farmsArray = Array.isArray(response.items) ? response.items : [];
          setFarms(farmsArray)      
        })
        .catch((err) => {
          log.error("Error fetching winery farms:", err);
        })
        .finally(() => setLoading(false)); 
  }, [location])


  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;
    const onChange = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };
    swiper.on("slideChange", onChange);
    onChange();
    return () => swiper.off("slideChange", onChange);
  }, [farms]);

  if (error) return <div className="p-8 text-center">Location error: {error}</div>

  return (
    <section className={FarmCardStyle["events-section"]}>
      <div className={FarmCardStyle["events-container"]}>
        <div className={FarmCardStyle["events-header"]}>
          <div>
            {loading
              ? <SkeletonCard variant="header" />
              : (
                <>
                  <h2 className={FarmCardStyle["events-title"]}>
                    {wineryCarouselContainerTitle} &#62;
                  </h2>
                  <p className={FarmCardStyle["events-subtitle"]}>
                    {wineryCarouselContainerSubtitle}
                  </p>
                </>
              )
            }
          </div>
          <CarouselNavigation 
            prevRef={prevRef}
            nextRef={nextRef}
            isBeginning={isBeginning}
            isEnd={isEnd}
          />
        </div>

        <SwiperFarmCardList 
          farms={farms}
          loading={loading}
          onFarmClick={farm => {
            const slug = slugify(farm.name || '', { lower: true, strict: true });
            navigate(`/farm/${farm.id}/${slug}`);
          }}          skeletonCount={6}
          prevRef={prevRef}
          nextRef={nextRef}
          updateNavState={updateNavState}
        />
      </div>
    </section>
  );
}

